import {
  type CreateProductLineBody,
  type ProductCenterProductsQuery,
  productCenterDimensionSchema,
  productCenterSortSchema,
} from "@adpilot/shared";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import { ReportingFilters } from "@/components/patterns/reporting-filters";
import {
  AllProductsView,
  PRODUCT_CENTER_METRICS,
  type ProductCenterFilters,
  type ProductCenterMetric,
} from "@/components/product-center/AllProductsView";
import { useShopScope } from "@/hooks/use-shop-scope";
import { fetchAmazonAccounts } from "@/lib/amazon-api";
import { getApiErrorCode, getApiErrorMessage } from "@/lib/api-error";
import { subscribeProductCenterRefresh } from "@/lib/data-status-events";
import {
  createProductLine,
  fetchProductCenterProducts,
  fetchProductLines,
} from "@/lib/product-center-api";
import { useAuthStore } from "@/stores/auth";
import { resolveReportingPeriod, useReportingPeriodStore } from "@/stores/reporting-period";
import { useReportingTimeZoneStore } from "@/stores/reporting-time-zone";

const allProductsSearchSchema = z.object({
  dimension: productCenterDimensionSchema.catch("SKU"),
  metric: z.enum(PRODUCT_CENTER_METRICS).catch("adSales"),
  granularity: z.enum(["day", "week", "month"]).catch("day"),
  search: z.string().trim().max(200).optional().catch(undefined),
  active: z.enum(["true", "false", "all"]).catch("true"),
  productStatus: z.enum(["all", "advertised", "unadvertised"]).catch("all"),
  productLineIds: z
    .preprocess(
      (value) => (typeof value === "string" ? value.split(",").filter(Boolean) : value),
      z.array(z.union([z.string().uuid(), z.literal("unassigned")])).max(50),
    )
    .optional()
    .catch(undefined),
  page: z.coerce.number().int().positive().catch(1),
  pageSize: z.coerce
    .number()
    .pipe(z.union([z.literal(10), z.literal(20), z.literal(50)]))
    .catch(20),
  sortBy: productCenterSortSchema.catch("identity"),
  sortDirection: z.enum(["asc", "desc"]).catch("asc"),
});

export const Route = createFileRoute("/_authed/all-products")({
  validateSearch: allProductsSearchSchema,
  component: AllProductsPage,
});

export function AllProductsPage(): React.ReactElement {
  const { t } = useTranslation("productCenter");
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();
  const workspaceId = useAuthStore((state) => state.user?.activeWorkspaceId);
  useEffect(() => {
    if (!workspaceId) return;
    return subscribeProductCenterRefresh(queryClient, workspaceId);
  }, [queryClient, workspaceId]);
  const reportingPeriod = useReportingPeriodStore((state) => state.period);
  const timeZone = useReportingTimeZoneStore((state) => state.timeZone);
  const { scopeShopIds, workspaceShops } = useShopScope();
  const accountsQuery = useQuery({
    queryKey: ["amazon", "accounts"],
    queryFn: fetchAmazonAccounts,
    staleTime: 60_000,
  });
  const marketByShop = new Map(
    accountsQuery.data?.connections.flatMap((connection) =>
      connection.shops.map((shop) => [shop.shopId, shop.marketplaceName] as const),
    ) ?? [],
  );
  const [targetShopId, setTargetShopId] = useState("");
  const creationShops = workspaceShops.filter((shop) => scopeShopIds.includes(shop.shopId));
  const creationShopId = creationShops.some((shop) => shop.shopId === targetShopId)
    ? targetShopId
    : (creationShops[0]?.shopId ?? "");
  const scopeKey = scopeShopIds.join(",");
  const previousScope = useRef(scopeKey);
  useEffect(() => {
    if (previousScope.current === scopeKey) return;
    previousScope.current = scopeKey;
    void navigate({
      replace: true,
      search: (previous) => ({ ...previous, page: 1, productLineIds: undefined }),
    });
  }, [scopeKey, navigate]);
  const resolvedPeriod = resolveReportingPeriod(reportingPeriod, new Date(), timeZone);
  const [searchDraft, setSearchDraft] = useState(search.search ?? "");
  const [candidateSearchDraft, setCandidateSearchDraft] = useState("");
  const [candidateSearch, setCandidateSearch] = useState("");
  const [isProductLineDialogOpen, setIsProductLineDialogOpen] = useState(false);

  useEffect(() => {
    setSearchDraft(search.search ?? "");
  }, [search.search]);

  useEffect(() => {
    if (searchDraft.trim() === (search.search ?? "")) return;
    const timeout = window.setTimeout(() => {
      const normalized = searchDraft.trim() || undefined;
      void navigate({
        replace: true,
        search: (previous) => ({ ...previous, search: normalized, page: 1 }),
      });
    }, 300);
    return () => window.clearTimeout(timeout);
  }, [navigate, search.search, searchDraft]);

  useEffect(() => {
    const timeout = window.setTimeout(() => setCandidateSearch(candidateSearchDraft.trim()), 300);
    return () => window.clearTimeout(timeout);
  }, [candidateSearchDraft]);

  const queryInput: ProductCenterProductsQuery = {
    shopIds: scopeShopIds,
    startDate: resolvedPeriod.startDate,
    endDate: resolvedPeriod.endDate,
    dimension: search.dimension,
    page: search.page,
    pageSize: search.pageSize,
    search: search.search,
    active: search.active,
    productStatus: search.productStatus === "all" ? undefined : search.productStatus,
    productLineIds: search.productLineIds?.length ? search.productLineIds : undefined,
    sortBy: search.sortBy,
    sortDirection: search.sortDirection,
  };
  const query = useQuery({
    queryKey: ["product-center", "products", scopeShopIds, queryInput],
    queryFn: ({ signal }) => fetchProductCenterProducts(queryInput, signal),
    enabled: scopeShopIds.length > 0,
  });
  const productLinesQuery = useQuery({
    queryKey: ["product-center", "product-lines", scopeShopIds],
    queryFn: ({ signal }) => fetchProductLines(signal, scopeShopIds),
    enabled: scopeShopIds.length > 0,
  });
  const productCandidatesQuery = useInfiniteQuery({
    queryKey: [
      "product-center",
      "product-line-candidates",
      creationShopId,
      resolvedPeriod.startDate,
      resolvedPeriod.endDate,
      candidateSearch,
    ],
    queryFn: ({ signal, pageParam }) =>
      fetchProductCenterProducts(
        {
          startDate: resolvedPeriod.startDate,
          endDate: resolvedPeriod.endDate,
          shopIds: [creationShopId],
          dimension: "ASIN",
          page: pageParam,
          pageSize: 100,
          search: candidateSearch.trim() || undefined,
          active: "all",
          sortBy: "identity",
          sortDirection: "asc",
        },
        signal,
      ),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.pagination.hasMore ? lastPage.pagination.page + 1 : undefined,
    enabled: Boolean(creationShopId) && isProductLineDialogOpen,
  });

  useEffect(() => {
    const returnedPage = query.data?.pagination.page;
    if (!returnedPage || returnedPage === search.page) return;
    void navigate({ replace: true, search: (previous) => ({ ...previous, page: returnedPage }) });
  }, [navigate, query.data?.pagination.page, search.page]);
  const createProductLineMutation = useMutation({
    mutationFn: ({ input, shopId }: { input: CreateProductLineBody; shopId: string }) =>
      createProductLine(input, shopId),
    onSuccess: async (productLine) => {
      toast.success(t("productLineDialog.created", { name: productLine.name }));
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["product-center", "product-lines"] }),
        queryClient.invalidateQueries({ queryKey: ["product-center", "products"] }),
        queryClient.invalidateQueries({
          queryKey: ["product-center", "product-line-candidates"],
        }),
      ]);
    },
    onError: async (error) => {
      const code = await getApiErrorCode(error);
      toast.error(
        code === "product_line.product_already_assigned"
          ? t("productLineDialog.alreadyAssigned")
          : code === "product_line.duplicate_name"
            ? t("productLineDialog.duplicateName")
            : code === "product_line.product_not_found"
              ? t("productLineDialog.productNotFound")
              : code === "product_line.not_found"
                ? t("productLineDialog.lineNotFound")
                : await getApiErrorMessage(error),
      );
    },
  });

  const updateFilters = (filters: ProductCenterFilters): void => {
    void navigate({
      replace: true,
      search: (previous) => ({ ...previous, ...filters, page: 1 }),
    });
  };

  return (
    <AllProductsView
      reportingFilters={<ReportingFilters />}
      creationShops={creationShops.map((shop) => ({
        id: shop.shopId,
        name: [shop.shopName, marketByShop.get(shop.shopId)].filter(Boolean).join(" · "),
      }))}
      creationShopId={creationShopId}
      onCreationShopChange={setTargetShopId}
      data={query.data}
      isLoading={query.isPending}
      isFetching={query.isFetching}
      isError={query.isError}
      dimension={search.dimension}
      granularity={search.granularity}
      metric={search.metric as ProductCenterMetric}
      search={searchDraft}
      filters={{ active: search.active, productStatus: search.productStatus }}
      productLines={productLinesQuery.data ?? []}
      isProductLinesLoading={productLinesQuery.isPending}
      isProductLinesError={productLinesQuery.isError}
      productLineCandidates={
        productCandidatesQuery.data?.pages.flatMap((candidatePage) => candidatePage.data) ?? []
      }
      isLoadingProductLineCandidates={productCandidatesQuery.isFetching}
      isProductLineCandidatesError={productCandidatesQuery.isError}
      hasMoreProductLineCandidates={productCandidatesQuery.hasNextPage ?? false}
      onProductLineCandidateSearch={setCandidateSearchDraft}
      onRetryProductLines={() => void productLinesQuery.refetch()}
      onLoadMoreProductLineCandidates={() => void productCandidatesQuery.fetchNextPage()}
      onRetryProductLineCandidates={() => void productCandidatesQuery.refetch()}
      onProductLineDialogOpenChange={setIsProductLineDialogOpen}
      productLineIds={search.productLineIds ?? []}
      sortBy={search.sortBy}
      sortDirection={search.sortDirection}
      page={query.data?.pagination.page ?? search.page}
      pageSize={search.pageSize}
      onDimensionChange={(dimension) =>
        void navigate({ search: (previous) => ({ ...previous, dimension, page: 1 }) })
      }
      onGranularityChange={(granularity) =>
        void navigate({ search: (previous) => ({ ...previous, granularity }) })
      }
      onMetricChange={(metric) =>
        void navigate({ search: (previous) => ({ ...previous, metric }) })
      }
      onSearchChange={setSearchDraft}
      onFiltersChange={updateFilters}
      onProductLineChange={(productLineIds) =>
        void navigate({
          replace: true,
          search: (previous) => ({
            ...previous,
            productLineIds: productLineIds.length ? productLineIds : undefined,
            page: 1,
          }),
        })
      }
      onCreateProductLine={(input) =>
        createProductLineMutation.mutateAsync({ input, shopId: creationShopId })
      }
      isCreatingProductLine={createProductLineMutation.isPending}
      onSortChange={(sortBy, sortDirection) =>
        void navigate({
          search: (previous) => ({ ...previous, sortBy, sortDirection, page: 1 }),
        })
      }
      onPageChange={(page) => void navigate({ search: (previous) => ({ ...previous, page }) })}
      onPageSizeChange={(pageSize) =>
        void navigate({ search: (previous) => ({ ...previous, pageSize, page: 1 }) })
      }
      onClearFilters={() => {
        setSearchDraft("");
        void navigate({
          replace: true,
          search: (previous) => ({
            ...previous,
            search: undefined,
            active: "true",
            productStatus: "all",
            productLineIds: undefined,
            page: 1,
          }),
        });
      }}
      onRetry={() => void query.refetch()}
    />
  );
}
